import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Category } from "@arc/contracts";
import type { AuthSession } from "../../auth/keycloak.js";
import { categoryApi } from "../../api/client.js";
import { invalidateManuals } from "./useManualMutations.js";

/** Typed mutation error — the ts-rest transport RESOLVES every response, so hooks inspect status + throw this. */
export interface CategoryMutationError extends Error {
  status: number;
  code?: string;
  /** The raw response body — preserved so the error dialog can render structured detail (e.g. attached facilities). */
  body?: unknown;
}
function fail(status: number, body: unknown): CategoryMutationError {
  const err = new Error(`category mutation failed (${status})`) as CategoryMutationError;
  err.status = status;
  err.code = (body as { code?: string } | undefined)?.code;
  err.body = body;
  return err;
}

const categoriesKey = (s: AuthSession | null) => ["categories", s?.realm, s?.sub];
const facilitiesKey = (s: AuthSession | null) => ["facilities", s?.realm, s?.sub];
const facilityCategoriesKey = (s: AuthSession | null, facilityId: number) => [
  "facility-categories",
  s?.realm,
  s?.sub,
  facilityId,
];

/** Create a realm-wide category. Invalidates the realm category list (the picker). */
export function useCreateCategory(session: AuthSession | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { name: string; description?: string | null }) => {
      const res = await categoryApi.createCategory({ body: vars });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body; // Category
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: categoriesKey(session) }),
  });
}

/**
 * Rename / re-describe / re-icon a realm category.
 *
 * `imageId` follows the contract's three states: **omit** to leave the icon alone, a **file id** to set it, **null**
 * to clear it. Never `0` — that is the legacy sentinel and the contract rejects it.
 *
 * `expectedUpdatedAt` is REQUIRED: it is the `updatedAt` of the Category this edit was composed against, so a
 * category someone else changed meanwhile 409s (`CATEGORY_STALE`) instead of being silently overwritten. Take it
 * from the Category object you are editing — never from a separately-fetched one, which defeats the point.
 */
export function useUpdateCategory(session: AuthSession | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: number; name?: string; description?: string | null; imageId?: number | null; expectedUpdatedAt: string }) => {
      const res = await categoryApi.updateCategory({ params: { id }, body });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body;
    },
    onSuccess: (data, vars) => {
      // Seed the cache with the row the server just returned, BEFORE the refetch lands (the same write-back
      // `usePatchRole` does). Without it the cached `updatedAt` stays stale for the whole round trip, so a user's own
      // second edit — a legitimate sequential change — submits the previous token and 409s against itself.
      qc.setQueryData<Category[]>(categoriesKey(session), (old) => old?.map((c) => (c.id === data.id ? data : c)));
      void qc.invalidateQueries({ queryKey: categoriesKey(session) });
      // A category's icon is the CARD IMAGE of every manual in it, across every facility the category is attached to
      // (`COALESCE(c.image_id, r.standard_category_icon_id)`), so an icon change invalidates far more than the picker.
      // Reuse the manual mutations' own helper rather than re-listing its keys here (ADR-043 Phase 2).
      if (vars.imageId !== undefined) {
        void qc.invalidateQueries({ queryKey: facilitiesKey(session) }); // facility cards embed category icons too
        invalidateManuals(qc, session);
      }
    },
  });
}

/** Soft-delete a realm category (409 while attached anywhere). */
export function useDeleteCategory(session: AuthSession | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await categoryApi.deleteCategory({ params: { id } });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: categoriesKey(session) }),
  });
}

/** Attach a category to a facility. Invalidates the facility's attached list + the tree (card categoryCount). */
export function useAttachCategory(session: AuthSession | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { facilityId: number; categoryId: number }) => {
      const res = await categoryApi.attachCategory({ params: { id: vars.facilityId }, body: { categoryId: vars.categoryId } });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body;
    },
    // RETURN the invalidations so the mutation stays pending until fresh membership lands — the caller's row keeps
    // its busy/disabled state across the refetch, closing the double-click window (panel).
    onSuccess: (_data, vars) =>
      Promise.all([
        qc.invalidateQueries({ queryKey: facilitiesKey(session) }), // card categoryCount
        qc.invalidateQueries({ queryKey: facilityCategoriesKey(session, vars.facilityId) }),
        qc.invalidateQueries({ queryKey: categoriesKey(session) }), // picker's per-category facilityCount
      ]),
  });
}

/** Detach a category from a facility (409 once it has manuals). Same invalidations as attach. */
export function useDetachCategory(session: AuthSession | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { facilityId: number; categoryId: number }) => {
      const res = await categoryApi.detachCategory({ params: { id: vars.facilityId, categoryId: vars.categoryId } });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body;
    },
    // RETURN the invalidations (see useAttachCategory) so the row stays busy until the refetch lands — a fast
    // second click can't re-detach an already-soft-deleted join and pop a spurious 404 (panel).
    onSuccess: (_data, vars) =>
      Promise.all([
        qc.invalidateQueries({ queryKey: facilitiesKey(session) }),
        qc.invalidateQueries({ queryKey: facilityCategoriesKey(session, vars.facilityId) }),
        qc.invalidateQueries({ queryKey: categoriesKey(session) }),
      ]),
  });
}
